import { type Registry as Services, service } from '@ember/service';
import Model, { type AsyncBelongsTo, attr, belongsTo } from '@ember-data/model';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { Type } from '@warp-drive/core-types/symbols';
import type { Dayjs } from 'dayjs';
import type {
  OtaId,
  Platform,
} from 'frontend-utils/utils/constants/constant-values';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import { CURRENCY_MAPPING } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { OtaHotelInfo } from 'lumen/models';
import type { MembershipType } from 'lumen/utils/constants/general-constants';
import { PLATFORM_TRANSLATION_KEYS } from 'lumen/utils/constants/general-constants';
import {
  STATUS_TRANSLATIONS,
  STATUSES,
  type StatusValue,
  TRANSLATIONS_MEMBERSHIP_TYPES,
} from 'lumen/utils/constants/pi-constant-values';

// Push to Parity Manager should be disabled for:
// • Chinese and Japanese OTAs (PI-723, PI-794)
const NON_ALLOWED_OTAS = [
  OTA_IDS.CTRIP,
  OTA_IDS.FLIGGY,
  OTA_IDS.MEITUAN,
  OTA_IDS.IKYU,
  OTA_IDS.JALAN,
  OTA_IDS.RAKUTEN,
];

export const PROMOTION_TYPES = {
  MOBILE_DISCOUNT: 'mobile',
  POS_DISCOUNT: 'pos',
  LOS_DISCOUNT: 'los',
  MEMBERSHIP_DISCOUNT: 'membership',
} as const;

export type PromotionType =
  (typeof PROMOTION_TYPES)[keyof typeof PROMOTION_TYPES];

// The status select sends '' when cleared
export type IssueStatus = StatusValue | '';

export const PROMOTION_TYPE_TRANSLATIONS = {
  [PROMOTION_TYPES.POS_DISCOUNT]: 'general.pos_short',
  [PROMOTION_TYPES.LOS_DISCOUNT]: 'rates.los',
} as const;

export const PROMOTION_TYPE_ICONS: Record<PromotionType, IconName> = {
  [PROMOTION_TYPES.MOBILE_DISCOUNT]: 'events-global',
  [PROMOTION_TYPES.POS_DISCOUNT]: 'location-pin-outline',
  [PROMOTION_TYPES.LOS_DISCOUNT]: 'moon-outline',
  [PROMOTION_TYPES.MEMBERSHIP_DISCOUNT]: 'badge-outline',
};

interface DebuggerInfo {
  formattedExtractDate: string;
  formattedFromDate: string;
  hotelinfoId: string;
  los: number;
  membershipType: MembershipType;
  ota: OtaId;
  persons: number;
  platform: Platform;
  subscriptionId: number;
}

export default class ParityStrategyIssue extends Model {
  [Type] = 'parity-strategy/issue' as const;

  @belongsTo('ota-hotel-info', { async: true, inverse: null })
  declare otaHotelInfo: AsyncBelongsTo<OtaHotelInfo>;

  @attr('boolean') declare brandAndChannelScreenshotPresent: boolean;
  @attr('number') declare brandRate: number;
  @attr('string') declare brandRateUrl: string;
  @attr('string') declare channel: OtaId;
  @attr('number') declare channelRate: number;
  @attr('string') declare channelRateUrl: string;
  @attr('string') declare currency: SupportedCurrencies;
  @attr('number') declare discountPercent: number;
  @attr('dayjs') declare extractDate: Dayjs;
  @attr('string') declare extranetUrl: string;
  @attr('dayjs') declare firstSpotCheckedArrivalDate: Dayjs;
  @attr('string') declare hotelinfoId: string;
  @attr('dayjs') declare lastSpotCheckedArrivalDate: Dayjs;
  @attr('number') declare los: number;
  @attr('string') declare membershipType: MembershipType;
  @attr('number') declare persons: number;
  @attr('number') declare platform: Platform;
  @attr('string') declare pos: string;
  @attr('string') declare promotionType: PromotionType;
  @attr('boolean') declare pushedToParityManager: boolean;
  @attr('string') declare screenshotUrl: string;
  @attr('string') declare status: IssueStatus;
  @attr('number') declare subscriptionId: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];

  get canBePushedToParityManager(): boolean {
    return (
      this.brandAndChannelScreenshotPresent &&
      this.screenshotUrl !== null &&
      !this.isNonAllowedOta
    );
  }

  get debuggerInfo(): DebuggerInfo {
    return {
      subscriptionId: this.subscriptionId,
      hotelinfoId: this.hotelinfoId,
      formattedExtractDate: this.formattedExtractDate,
      formattedFromDate: this.lastSpotCheckedArrivalDate.format('YYYY-MM-DD'),
      ota: this.channel,
      los: this.los,
      persons: this.persons,
      membershipType: this.membershipType,
      platform: this.platform,
    };
  }

  get formattedBrandRate(): string {
    return this.formatRateWithCustomSymbol(this.currency, this.brandRate);
  }

  get formattedChannelRate(): string {
    return this.formatRateWithCustomSymbol(this.currency, this.channelRate);
  }

  get formattedDiscount(): string {
    return this.intl.formatNumber(this.discountPercent, { style: 'percent' });
  }

  get formattedExtractDate(): string {
    return this.extractDate.format('YYYY-MM-DD');
  }

  get formattedFirstSpotCheckedArrivalDate(): string {
    return this.firstSpotCheckedArrivalDate.format('ll');
  }

  get formattedLastSpotCheckedArrivalDate(): string {
    return this.lastSpotCheckedArrivalDate.format('ll');
  }

  get formattedLos(): string {
    return this.intl.t('general.days.count', { count: this.los });
  }

  get formattedPlatform(): string {
    switch (this.promotionType) {
      case PROMOTION_TYPES.MOBILE_DISCOUNT:
        return this.intl.t(PLATFORM_TRANSLATION_KEYS[this.platform]);
      case PROMOTION_TYPES.MEMBERSHIP_DISCOUNT:
        return this.intl.t(TRANSLATIONS_MEMBERSHIP_TYPES[this.membershipType]!);
      default:
        return this.intl.t(PROMOTION_TYPE_TRANSLATIONS[this.promotionType]);
    }
  }

  get formattedStatus(): string {
    if (this.status === '' || this.status === STATUSES.UNKNOWN) {
      return this.intl.t('general.missing');
    }
    return this.intl.t(STATUS_TRANSLATIONS[this.status]);
  }

  get formattedValue(): string {
    if (!this.isScreenshotSupported) {
      return this.intl.t('general.not_applicable');
    }

    return this.intl.t('general.screenshot');
  }

  get isNonAllowedOta(): boolean {
    return (NON_ALLOWED_OTAS as string[]).includes(this.channel);
  }

  get isScreenshotSupported(): boolean {
    const supportedScreenshotPlatforms =
      this.otaHotelInfo.get('supportedScreenshotPlatforms') ?? [];
    const isPlatformSupportedForCurrentSubscriptionAndOTA =
      supportedScreenshotPlatforms.includes(this.platform);

    return isPlatformSupportedForCurrentSubscriptionAndOTA;
  }

  get platformIcon(): IconName {
    return PROMOTION_TYPE_ICONS[this.promotionType];
  }

  // Workaround for formatting a rate with a custom currency: fetch the currency symbol + format the rate with includeSymbol=false
  formatRateWithCustomSymbol(
    currencyCode: keyof typeof CURRENCY_MAPPING,
    rate: number,
  ): string {
    return `${CURRENCY_MAPPING[currencyCode]} ${this.hotelAndMarketService.formatRate(
      rate,
      { includeCurrencySymbol: false },
    )}`;
  }
}
